import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { withRouter } from 'react-router';
import RouterPropTypes from 'react-router-prop-types';
import {
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
} from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import FormField from '../../../models/FormField';
import { modelOf } from '../../../prop-types';
import { roundWithPrecision } from '../../../util/number';
import { parse } from '../../../util/queryString';
import Field from '../../form/Field';
import Icon from '../Icon';

const QUANTITY_FIELD_GROUP_UNIQUE_ID = 'QuantityFieldGroup--';

export const QuantityFieldGroup = observer(
  ({
    className,
    decrementQuantity: customDecrementQuantity,
    incrementQuantity: customIncrementQuantity,
    intl,
    disabled,
    field,
    maxQuantity,
    unit,
    balanceUserAccount,
    children,
    onBlur,
    onFocus,
    quantityStep,
    incrementStep,
    onUpdateProductQuantity,
    onBlurProductQuantity,
    onPreviewProductQuantity,
    updateOnBlur,
    location,
    productPageContent,
  }) => {
    // Generate unique ID for this instance
    const fieldId = useMemo(() => uniqueId(QUANTITY_FIELD_GROUP_UNIQUE_ID), []);

    // Get step value (incrementStep takes precedence over quantityStep)
    const step = useMemo(
      () => incrementStep || quantityStep,
      [incrementStep, quantityStep]
    );

    // Helper function to count balance for user account
    const countBalanceUserBalance = useCallback(
      (precicionValue, precicionStep, newValue) => {
        let adjustedValue = newValue;

        if (
          precicionValue < precicionStep ||
          precicionValue - precicionStep < precicionStep
        ) {
          adjustedValue = precicionStep;
        }

        if (precicionValue === precicionStep) {
          adjustedValue = adjustedValue - precicionStep;
        }

        return adjustedValue;
      },
      []
    );

    // Update product quantity callback
    const updateProductQuantity = useCallback(
      (value) => {
        if (onUpdateProductQuantity) {
          onUpdateProductQuantity(Number(value));
        }
      },
      [onUpdateProductQuantity]
    );

    // Handle increment
    const handleIncrementQuantity = useCallback(() => {
      const factor = 100;
      const precicionValue = roundWithPrecision(
        roundWithPrecision(Number(field.value), 2) * factor,
        0
      );
      const precicionStep = roundWithPrecision(
        roundWithPrecision(step, 2) * factor,
        0
      );
      const newValue =
        (Math.floor((precicionValue + precicionStep) / precicionStep) *
          precicionStep) /
        factor;
      const roundedValue = roundWithPrecision(newValue, 2);
      const stringValue = String(roundedValue);

      if (roundedValue <= maxQuantity) {
        field.setValue(stringValue);
      }
      updateProductQuantity(roundedValue);
    }, [field, maxQuantity, step, updateProductQuantity]);

    // Handle decrement
    const handleDecrementQuantity = useCallback(() => {
      const factor = 100;
      const precicionValue = roundWithPrecision(
        roundWithPrecision(Number(field.value), 2) * factor,
        0
      );
      const precicionStep = roundWithPrecision(
        roundWithPrecision(step, 2) * factor,
        0
      );

      const precicionMax = roundWithPrecision(
        roundWithPrecision(maxQuantity, 2) * factor,
        0
      );

      let newValue;

      if (
        precicionValue < precicionStep ||
        precicionValue - precicionStep < precicionStep
      ) {
        newValue = precicionStep;
      } else if (precicionValue > precicionMax) {
        newValue = precicionMax;
      } else if (
        precicionValue > precicionStep &&
        precicionValue % precicionStep
      ) {
        const nextIncrementalValue =
          Math.floor((precicionValue + precicionStep) / precicionStep) *
          precicionStep;
        const nextIncrementalValueStep =
          nextIncrementalValue -
          Math.floor((precicionValue - precicionStep) / precicionStep) *
            precicionStep;
        newValue = nextIncrementalValue - nextIncrementalValueStep / 2;
      } else {
        newValue = precicionValue - precicionStep;
      }

      if (balanceUserAccount) {
        newValue = countBalanceUserBalance(
          precicionValue,
          precicionStep,
          newValue
        );
      }

      const roundedWithPrecisionValue = String(
        roundWithPrecision(newValue / factor, 2)
      );
      field.setValue(roundedWithPrecisionValue);
      updateProductQuantity(roundedWithPrecisionValue);
    }, [
      field,
      maxQuantity,
      step,
      balanceUserAccount,
      countBalanceUserBalance,
      updateProductQuantity,
    ]);

    const handleBlurProductQuantity = useCallback(() => {
      if (onBlurProductQuantity) {
        onBlurProductQuantity(Number(field.value));
      }
    }, [field.value, onBlurProductQuantity]);

    // Handle onChange event
    const handleChangeProductQuantity = useCallback(
      (e) => {
        const newValue = e.target.value;
        field.setValue(String(newValue));

        const numericValue = Number(newValue);
        if (onPreviewProductQuantity) {
          onPreviewProductQuantity(numericValue);
        }

        if (!updateOnBlur) {
          updateProductQuantity(numericValue);
        }
      },
      [field, onPreviewProductQuantity, updateOnBlur, updateProductQuantity]
    );

    // Handle focus quantity input on disabled state change
    useEffect(() => {
      const queryParams = parse(location.search);
      const focusQuantity = queryParams.focusQuantity;

      if (focusQuantity && productPageContent) {
        const element = document.querySelector(
          '.QuantityFieldGroup__input-group input'
        );

        if (element) {
          element.scrollIntoView({ block: 'center' });
          element.focus();
        }
      }
    }, [disabled, location.search, productPageContent]);

    // Calculate decrement button disabled state
    const disableDecrementButton = useMemo(
      () =>
        balanceUserAccount && field.value > 0 ? false : field.value <= step,
      [balanceUserAccount, field.value, step]
    );

    // Format the field value to ensure it displays with proper precision (max 2 decimals)
    const displayValue = useMemo(
      () =>
        field.value
          ? String(roundWithPrecision(Number(field.value), 2))
          : field.value,
      [field.value]
    );

    return (
      <div
        className={classNames('QuantityFieldGroup', className, {
          'QuantityFieldGroup--disabled': disabled,
        })}
      >
        <Label for={fieldId} hidden>
          <FormattedMessage {...globalTranslations.quantityTitle} />
        </Label>
        <InputGroup className="QuantityFieldGroup__input-group">
          <InputGroupAddon addonType="prepend">
            <Button
              size="sm"
              color="secondary"
              disabled={disabled || disableDecrementButton}
              onClick={customDecrementQuantity || handleDecrementQuantity}
              aria-label={intl.formatMessage(
                globalTranslations.quantityDecrementTitle
              )}
            >
              <Icon name="minus" />
            </Button>
          </InputGroupAddon>
          <Field
            component={Input}
            field={field}
            handleBlur={handleBlurProductQuantity}
            className="QuantityFieldGroup__quantity"
            step={step}
            type="number"
            min="1"
            max={maxQuantity}
            disabled={disabled}
            pattern="[0-9]*"
            id={fieldId}
            value={displayValue}
            onChange={handleChangeProductQuantity}
            onBlur={onBlur && onBlur}
            onFocus={onFocus && onFocus}
          />
          <InputGroupAddon addonType="append">
            {unit && (
              <InputGroupText className="QuantityFieldGroup__stock-unit">
                {unit}
              </InputGroupText>
            )}
            <Button
              size="sm"
              color="secondary"
              disabled={disabled || field.value >= maxQuantity}
              onClick={customIncrementQuantity || handleIncrementQuantity}
              aria-label={intl.formatMessage(
                globalTranslations.quantityIncrementTitle
              )}
            >
              <Icon name="plus" />
            </Button>
          </InputGroupAddon>
        </InputGroup>
        {children}
      </div>
    );
  }
);

QuantityFieldGroup.propTypes = {
  intl: intlShape.isRequired,
  field: modelOf(FormField).isRequired,
  location: RouterPropTypes.location.isRequired,
  className: PropTypes.string,
  unit: PropTypes.string,
  maxQuantity: PropTypes.number,
  quantityStep: PropTypes.number,
  incrementStep: PropTypes.number,
  defaultValue: PropTypes.number,
  balanceUserAccount: PropTypes.bool,
  disabled: PropTypes.bool,
  overrideOnUpdate: PropTypes.bool,
  productPageContent: PropTypes.bool,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onUpdateProductQuantity: PropTypes.func,
  onBlurProductQuantity: PropTypes.func,
  onPreviewProductQuantity: PropTypes.func,
  incrementQuantity: PropTypes.func,
  decrementQuantity: PropTypes.func,
  updateOnBlur: PropTypes.bool,
};

QuantityFieldGroup.defaultProps = {
  maxQuantity: 1,
  quantityStep: 1,
  balanceUserAccount: false,
  updateOnBlur: false,
};

export default injectIntl(withRouter(QuantityFieldGroup));
